/**
 * Non-hook API helpers.
 *
 * JSON endpoints are consumed in components via the orval-generated React Query
 * hooks (`@/lib/api/orval/target/...`). This module keeps:
 *   - plain async fns used by non-React code (the auth-session bootstrap), and
 *   - the multipart image upload, which doesn't fit the JSON fetch mutator.
 */

import { buildConfig } from '@/config';
import type { MachineImage } from '@/lib/types';

// Re-export the generated plain (non-hook) functions used outside React.
export { getCurrentUser, logout } from '@/lib/api/orval/target/auth/auth';

const BASE = buildConfig.apiBaseUrl; // "/api/v1"

/**
 * Upload a machine screenshot via multipart/form-data.
 *
 * The orval/customFetch path JSON-encodes bodies, so file uploads use fetch
 * directly. Returns the created image metadata.
 */
export async function uploadMachineImage(
  publicId: string,
  file: File,
  caption = '',
): Promise<MachineImage> {
  const form = new FormData();
  form.append('image', file);
  if (caption) form.append('caption', caption);

  const response = await fetch(`${BASE}/machines/${publicId}/images/upload`, {
    method: 'POST',
    body: form,
    credentials: 'include',
  });
  if (!response.ok) {
    let message = 'Upload failed';
    try {
      const data = await response.json();
      message = data.error || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return response.json();
}

/** Delete a machine image. */
export async function deleteMachineImage(
  publicId: string,
  imageId: string,
): Promise<void> {
  const response = await fetch(
    `${BASE}/machines/${publicId}/images/${imageId}`,
    { method: 'DELETE', credentials: 'include' },
  );
  if (!response.ok) throw new Error('Delete failed');
}

/** Designate a machine image as the main one. */
export async function designateMainImage(
  publicId: string,
  imageId: string,
): Promise<void> {
  const response = await fetch(
    `${BASE}/machines/${publicId}/images/${imageId}`,
    { method: 'POST', credentials: 'include' },
  );
  if (!response.ok) throw new Error('Update failed');
}
